/**
 * Safety / content-filter rules per child.
 *
 * Three tabs: Categories, Lists (block + allow), Recent activity.
 * - Categories: instant-save toggles. Optimistic update locally; the server
 *   returns the full category-rule list and that becomes the source of truth.
 * - Lists: domain entries with optional notes; deletes are immediate.
 * - Activity: paginated audit feed (50 most recent).
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, ShieldCheck, ShieldOff, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { usersApi } from '@/api/users';
import {
  safetyApi,
  type AllowlistEntry,
  type BlockEvent,
  type BlocklistEntry,
  type Category,
  type CategoryRule,
  type ChildRules,
} from '@/api/safety';
import { ChildPicker } from './ChildPicker';
import { PageHeader } from './PageHeader';

export function SafetyPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const children = useQuery({ queryKey: ['children'], queryFn: () => usersApi.listChildren() });
  const categories = useQuery({
    queryKey: ['safety', 'categories'],
    queryFn: () => safetyApi.listCategories(),
    staleTime: 60 * 60_000, // 1h — categories rarely change
  });
  const [selectedChildId, setSelectedChildId] = useState<string | null>(null);

  const rules = useQuery({
    queryKey: ['safety', 'rules', selectedChildId],
    queryFn: () => safetyApi.getRules(selectedChildId!),
    enabled: !!selectedChildId,
  });
  const events = useQuery({
    queryKey: ['safety', 'events', selectedChildId],
    queryFn: () => safetyApi.listEvents(selectedChildId!, 50),
    enabled: !!selectedChildId,
  });

  const updateCategory = useMutation({
    mutationFn: ({ slug, blocked }: { slug: string; blocked: boolean }) =>
      safetyApi.updateCategories(selectedChildId!, [{ slug, blocked }]),
    onSuccess: (newRules) => {
      // Server returns the full category-rule list — patch the rules cache.
      queryClient.setQueryData<ChildRules>(
        ['safety', 'rules', selectedChildId],
        (old) => (old ? { ...old, categories: newRules } : old),
      );
    },
    onError: () => toast.error('Could not update rule.'),
  });

  const childOptions = children.data ?? [];

  return (
    <div className="px-6 py-8 md:px-10 md:py-10">
      <PageHeader
        title={t('pages.safety.title')}
        icon={ShieldCheck}
        tint="green"
        description={t('pages.safety.description')}
      />

      <div className="mt-6">
        <ChildPicker
          children={childOptions}
          selectedId={selectedChildId}
          onSelect={setSelectedChildId}
        />
      </div>

      {!selectedChildId && (
        <Card className="mt-6">
          <CardContent className="p-8 text-center text-sm text-[color:var(--kz-color-ink-secondary)]">
            {childOptions.length === 0 ? 'Add a child first.' : 'Select a child to edit safety rules.'}
          </CardContent>
        </Card>
      )}

      {selectedChildId && (
        <Tabs defaultValue="categories" className="mt-6">
          <TabsList>
            <TabsTrigger value="categories">Categories</TabsTrigger>
            <TabsTrigger value="lists">Lists</TabsTrigger>
            <TabsTrigger value="activity">Recent activity</TabsTrigger>
          </TabsList>

          <TabsContent value="categories">
            <CategoriesPanel
              categories={categories.data ?? []}
              rules={rules.data?.categories ?? []}
              onToggle={(slug, blocked) => updateCategory.mutate({ slug, blocked })}
              isPending={updateCategory.isPending}
            />
          </TabsContent>

          <TabsContent value="lists">
            <ListsPanel
              childId={selectedChildId}
              blocklist={rules.data?.blocklist ?? []}
              allowlist={rules.data?.allowlist ?? []}
            />
          </TabsContent>

          <TabsContent value="activity">
            <ActivityPanel events={events.data ?? []} loading={events.isPending} />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}

// ── Categories ────────────────────────────────────────────────────────────────

function CategoriesPanel({
  categories,
  rules,
  onToggle,
  isPending,
}: {
  categories: Category[];
  rules: CategoryRule[];
  onToggle: (slug: string, blocked: boolean) => void;
  isPending: boolean;
}) {
  const ruleBySlug = new Map(rules.map((r) => [r.slug, r]));
  return (
    <Card>
      <CardHeader>
        <CardTitle>Category rules</CardTitle>
        <CardDescription>
          When a category is on, sites in that group are blocked. Adult / gambling / violence / drugs
          are blocked by default for every plan.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ul className="grid gap-2">
          {categories.map((cat) => {
            const r = ruleBySlug.get(cat.slug);
            const blocked = r?.blocked ?? cat.default_block;
            return (
              <li
                key={cat.slug}
                className="flex items-start gap-4 rounded-md border border-[color:var(--kz-color-border-subtle)] bg-[color:var(--kz-color-surface-card)] p-4"
              >
                <span
                  className={`grid size-9 shrink-0 place-items-center rounded-full ${blocked ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700'}`}
                >
                  {blocked ? <ShieldOff className="size-4" /> : <ShieldCheck className="size-4" />}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium leading-tight">{cat.name}</p>
                  <p className="mt-0.5 text-xs text-[color:var(--kz-color-ink-secondary)]">
                    {cat.description}
                  </p>
                </div>
                <Switch
                  checked={blocked}
                  disabled={isPending}
                  onCheckedChange={(v) => onToggle(cat.slug, v)}
                  aria-label={`Block ${cat.name}`}
                />
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
}

// ── Lists ─────────────────────────────────────────────────────────────────────

function ListsPanel({
  childId,
  blocklist,
  allowlist,
}: {
  childId: string;
  blocklist: BlocklistEntry[];
  allowlist: AllowlistEntry[];
}) {
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <DomainList
        title="Blocklist"
        description="Domains the child can never reach, even if their category is allowed."
        entries={blocklist}
        emptyHint="No domains blocked."
        add={(domain, note) => safetyApi.addBlocklist(childId, domain, note)}
        remove={(id) => safetyApi.removeBlocklist(childId, id)}
        cacheKey={['safety', 'rules', childId]}
      />
      <DomainList
        title="Allowlist"
        description="Domains always allowed — overrides categories and blocklist."
        entries={allowlist}
        emptyHint="No exceptions added."
        add={(domain, note) => safetyApi.addAllowlist(childId, domain, note)}
        remove={(id) => safetyApi.removeAllowlist(childId, id)}
        cacheKey={['safety', 'rules', childId]}
      />
    </div>
  );
}

function DomainList<T extends { id: string; domain: string; note: string | null; created_at: string }>({
  title,
  description,
  entries,
  emptyHint,
  add,
  remove,
  cacheKey,
}: {
  title: string;
  description: string;
  entries: T[];
  emptyHint: string;
  add: (domain: string, note?: string) => Promise<T>;
  remove: (id: string) => Promise<void>;
  cacheKey: (string | null)[];
}) {
  const queryClient = useQueryClient();
  const [domain, setDomain] = useState('');
  const [note, setNote] = useState('');

  const addMut = useMutation({
    mutationFn: () => add(domain.trim(), note.trim() || undefined),
    onSuccess: () => {
      setDomain('');
      setNote('');
      void queryClient.invalidateQueries({ queryKey: cacheKey });
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Could not add.'),
  });

  const removeMut = useMutation({
    mutationFn: (id: string) => remove(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: cacheKey }),
    onError: () => toast.error('Could not remove.'),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (domain.trim()) addMut.mutate();
          }}
          className="grid gap-2"
        >
          <div className="grid gap-1.5">
            <Label htmlFor={`${title}-domain`}>Domain</Label>
            <Input
              id={`${title}-domain`}
              placeholder="example.com"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor={`${title}-note`}>Note (optional)</Label>
            <Input
              id={`${title}-note`}
              placeholder="reason or context"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
          <Button type="submit" size="sm" disabled={!domain.trim() || addMut.isPending} className="justify-self-start">
            <Plus className="size-4" />
            Add
          </Button>
        </form>

        <ul className="space-y-2">
          {entries.length === 0 && (
            <li className="rounded-md border border-dashed border-[color:var(--kz-color-border-subtle)] p-3 text-xs text-[color:var(--kz-color-ink-muted)]">
              {emptyHint}
            </li>
          )}
          {entries.map((e) => (
            <li
              key={e.id}
              className="flex items-center gap-3 rounded-md border border-[color:var(--kz-color-border-subtle)] bg-[color:var(--kz-color-surface-card)] p-3"
            >
              <div className="flex-1 min-w-0">
                <p className="truncate font-mono text-sm">{e.domain}</p>
                {e.note && (
                  <p className="truncate text-xs text-[color:var(--kz-color-ink-muted)]">{e.note}</p>
                )}
              </div>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Remove"
                onClick={() => removeMut.mutate(e.id)}
                disabled={removeMut.isPending}
              >
                <Trash2 className="size-4" />
              </Button>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

// ── Activity ──────────────────────────────────────────────────────────────────

function ActivityPanel({
  events,
  loading,
}: {
  events: BlockEvent[];
  loading: boolean;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Recent activity</CardTitle>
        <CardDescription>50 most recent decisions logged by the child device.</CardDescription>
      </CardHeader>
      <CardContent>
        {loading && (
          <div className="space-y-2">
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </div>
        )}
        {!loading && events && events.length === 0 && (
          <p className="rounded-md border border-dashed border-[color:var(--kz-color-border-subtle)] p-3 text-xs text-[color:var(--kz-color-ink-muted)]">
            No activity yet.
          </p>
        )}
        {!loading && events && events.length > 0 && (
          <ul className="divide-y divide-[color:var(--kz-color-border-subtle)]">
            {events.map((ev) => (
              <li key={ev.id} className="flex items-center gap-3 py-2.5">
                <span
                  className={
                    ev.decision === 'blocked'
                      ? 'inline-flex shrink-0 items-center rounded-full bg-rose-100 px-2 py-0.5 text-xs font-medium text-rose-800'
                      : 'inline-flex shrink-0 items-center rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800'
                  }
                >
                  {ev.decision}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="truncate font-mono text-sm">{ev.domain}</p>
                  <p className="text-xs text-[color:var(--kz-color-ink-muted)]">
                    {ev.reason}
                    {ev.category_slug && ` · ${ev.category_slug}`}
                  </p>
                </div>
                <span className="shrink-0 text-xs tabular-nums text-[color:var(--kz-color-ink-muted)]">
                  {new Date(ev.occurred_at).toLocaleString()}
                </span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
